import { isCameraStreamLogsEnabled } from 'services/CameraStream/config';
import { ACTION_ORDER, ACTION_TYPE, SKIP_ACTIONS } from './configuration';

export default class ActionScheduler {
    #actions = new Map();
    #cameraStream;
    #isPaused = true;
    #isProcessing;
    #isInitiated = false;
    #fullHeader;
    #actionCompletionTimeout = 10000;

    constructor(cameraStream) {
        this.#cameraStream = cameraStream;
        this.#fullHeader = `[ActionScheduler cameraId=${cameraStream.cameraId}]`;
    }

    schedule(action, args) {
        if (SKIP_ACTIONS.includes(action)) return;
        this.#register(action, args);
        if (this.#isInitiated) return;
        this.#cameraStream.initiateKurentoClient();
    }

    scheduleAndWait(action, args) {
        if (SKIP_ACTIONS.includes(action)) return Promise.resolve();
        const { resolve, promise: completionPromise } = this.#completionPromiseFactory();

        const onRejection = () =>
            this.#log(
                `scheduleAndWait(): Scheduled action ${action} not completed in ${this.#actionCompletionTimeout}ms, rejecting caller's promise`,
                'error'
            );
        const promise = this.#rejectAfter(this.#actionCompletionTimeout, completionPromise).catch(onRejection);
        this.#register(action, args, resolve, this.#actionCompletionTimeout);
        if (this.#isInitiated) return promise;
        this.#cameraStream.initiateKurentoClient();
        return promise;
    }

    #register(action, actionArguments, onCompletion) {
        this.#log(`#register(): Registering actionName=${action} with arguments ${JSON.stringify(actionArguments)}`);

        const currentAction = this.#actions.get(action);

        if (!currentAction) this.#actions.set(action, new ACTION_TYPE[action](this.#cameraStream, action, actionArguments, onCompletion));
        else currentAction.update(actionArguments, onCompletion);

        if (!this.#isPaused && !this.#isProcessing) this.process();
    }

    async process() {
        if (this.#isPaused || this.#isProcessing) return;
        const { action } = this.#getNextAction();
        if (!action) return;
        this.#isProcessing = true;
        try {
            this.#log(`process(): starting to process action=${action}`);
            await this.#actions.get(action).execute();
            this.#log(`process(): processing of action=${action} finished`);
        } catch (err) {
            this.#log(`process(): Error processing action=${action}, message=${err.message}`, 'error');
        }
        this.#isProcessing = false;
        this.process();
    }

    #getNextAction() {
        for (let i = 0; i < ACTION_ORDER.length; i++) {
            const action = ACTION_ORDER[i];
            const nextAction = this.#actions.get(action);
            if (nextAction?.shouldExecute) return { action };
        }
        return {};
    }
    pause() {
        this.#isPaused = true;
    }

    start = () => {
        this.#isPaused = false;
        this.#isInitiated = true;
        this.process();
    };

    isInitiated() {
        return this.#isInitiated;
    }

    #log(...args) {
        const validModes = ['log', 'error', 'warn', 'info', 'debug'];
        const lastArg = args[args.length - 1];
        const mode = typeof lastArg === 'string' && validModes.includes(lastArg) ? args.pop() : 'log';
        if (!isCameraStreamLogsEnabled(mode)) return;

        const serializedArgs = args.map((arg) => {
            if (typeof arg === 'object' && arg !== null) {
                try {
                    return JSON.stringify(arg);
                    // eslint-disable-next-line
                } catch (_) {
                    return '[Object - serialization failed]';
                }
            }
            return arg;
        });

        console[mode](`${this.#fullHeader}`, ...serializedArgs);
    }

    #completionPromiseFactory() {
        return Promise.withResolvers();
    }

    #rejectAfter = (miliseconds, resolveOn, rejectMessage) =>
        new Promise((res, rej) => {
            const timeoutId = setTimeout(() => {
                rej(rejectMessage);
                rejectMessage && this.#cameraStream.streamStateManager.setInfo(rejectMessage);
            }, miliseconds);

            resolveOn &&
                resolveOn.then(() => {
                    clearTimeout(timeoutId);
                    res();
                });
        });
}
